<template>
  <div class="app-shell" :class="{ 'is-collapsed': navigationCollapsed }">
    <aside class="sidebar">
      <RouterLink to="/patients" class="brand">
        <span class="brand__mark">华</span>
        <span class="brand__name">华阳羿鑫 · 管理后台</span>
      </RouterLink>

      <el-scrollbar class="sidebar__scroll">
        <nav class="sidebar-navigation" aria-label="主导航">
          <template v-for="(group, groupIndex) in visibleNavigationGroups" :key="group.title ?? groupIndex">
            <div v-if="group.title && !navigationCollapsed" class="menu-group-title">
              {{ group.title }}
            </div>
            <RouterLink
              v-for="item in group.items"
              :key="item.path"
              :to="item.path"
              class="sidebar-menu-link"
              :class="{ 'is-active': activePath === item.path }"
              :aria-label="item.label"
              :aria-current="activePath === item.path ? 'page' : undefined"
              :title="navigationCollapsed ? item.label : undefined"
            >
              <el-icon><component :is="item.icon" /></el-icon>
              <span v-if="!navigationCollapsed" class="sidebar-menu-label">{{ item.label }}</span>
              <span v-if="item.badge" class="menu-badge">{{ item.badge }}</span>
            </RouterLink>
          </template>
        </nav>
      </el-scrollbar>

      <div class="sidebar-user">
        <div class="avatar">{{ session.user?.avatarText ?? '王' }}</div>
        <div v-if="!navigationCollapsed" class="sidebar-user__text">
          <strong>{{ session.user?.name ?? '加载中' }}</strong>
          <span>{{ session.user?.role ?? '获取用户信息' }}</span>
        </div>
      </div>
    </aside>

    <div class="app-main">
      <header class="topbar">
        <button class="topbar-button mobile-menu-button" type="button" aria-label="打开导航菜单" @click="layout.mobileMenuVisible = true">
          <el-icon><Menu /></el-icon>
        </button>
        <button class="topbar-button collapse-button" type="button" aria-label="收起或展开侧栏" @click="layout.toggleCollapsed">
          <el-icon><Fold v-if="!navigationCollapsed" /><Expand v-else /></el-icon>
        </button>

        <el-breadcrumb separator="/">
          <el-breadcrumb-item>{{ route.meta.group ?? '管理后台' }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ route.meta.title }}</el-breadcrumb-item>
        </el-breadcrumb>

        <div class="topbar__actions">
          <el-tooltip content="刷新当前页面" placement="bottom">
            <button class="topbar-button" type="button" aria-label="刷新当前页面" @click="refreshPage">
              <el-icon><Refresh /></el-icon>
            </button>
          </el-tooltip>

          <el-popover placement="bottom-end" :width="360" trigger="click">
            <template #reference>
              <el-badge :value="session.unreadCount" :hidden="session.unreadCount === 0" :max="99">
                <button class="topbar-button" type="button" aria-label="通知">
                  <el-icon><Bell /></el-icon>
                </button>
              </el-badge>
            </template>
            <div class="notification-header">
              <strong>消息通知</strong>
              <span>{{ session.unreadCount }} 条未读</span>
            </div>
            <div v-if="session.notifications.length" class="notification-list">
              <button
                v-for="item in session.notifications"
                :key="item.id"
                class="notification-item"
                :class="{ 'is-unread': !item.read }"
                type="button"
                @click="readNotification(item.id)"
              >
                <span class="notification-item__dot" :class="`is-${item.level}`"></span>
                <span class="notification-item__body">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.content }}</span>
                  <small>{{ item.time }}</small>
                </span>
              </button>
            </div>
            <el-empty v-else description="暂无通知" :image-size="58" />
          </el-popover>

          <el-dropdown>
            <button class="profile-button" type="button">
              <span class="avatar">{{ session.user?.avatarText ?? '王' }}</span>
              <span class="profile-button__name">{{ session.user?.name ?? '加载中' }}</span>
              <el-icon><ArrowDown /></el-icon>
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item :icon="User" @click="passwordDialogVisible = true">修改密码</el-dropdown-item>
                <el-dropdown-item divided :icon="SwitchButton" @click="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <main class="app-content">
        <RouterView v-slot="{ Component, route: viewRoute }">
          <Transition name="page" mode="out-in">
            <component :is="Component" :key="routeComponentKey(viewRoute)" />
          </Transition>
        </RouterView>
      </main>
    </div>

    <el-drawer
      v-model="layout.mobileMenuVisible"
      direction="ltr"
      size="270px"
      :with-header="false"
      class="mobile-navigation"
    >
      <RouterLink to="/patients" class="mobile-brand" @click="layout.mobileMenuVisible = false">
        <span class="brand__mark">华</span>
        <span>华阳羿鑫 · 管理后台</span>
      </RouterLink>
      <el-scrollbar class="mobile-navigation__scroll">
        <nav class="mobile-navigation__menu" aria-label="移动主导航">
          <template v-for="(group, groupIndex) in visibleNavigationGroups" :key="`mobile-${group.title ?? groupIndex}`">
            <div v-if="group.title" class="mobile-menu-title">{{ group.title }}</div>
            <RouterLink
              v-for="item in group.items"
              :key="item.path"
              :to="item.path"
              class="mobile-menu-link"
              :class="{ 'is-active': activePath === item.path }"
              :aria-current="activePath === item.path ? 'page' : undefined"
              @click="closeMobileMenuOnPrimaryNavigation"
            >
              <el-icon><component :is="item.icon" /></el-icon>
              <span>{{ item.label }}</span>
              <span v-if="item.badge" class="menu-badge">{{ item.badge }}</span>
            </RouterLink>
          </template>
        </nav>
      </el-scrollbar>
      <div class="mobile-navigation__user">
        <span class="avatar">{{ session.user?.avatarText ?? '王' }}</span>
        <span><strong>{{ session.user?.name ?? '加载中' }}</strong><small>{{ session.user?.role ?? '' }}</small></span>
      </div>
    </el-drawer>

    <ChangeMyPasswordDialog
      v-model="passwordDialogVisible"
      :submitting="passwordSubmitting"
      @save="changePassword"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter, RouterLink, RouterView, type RouteLocationNormalizedLoaded } from 'vue-router'
import {
  ArrowDown,
  Bell,
  Expand,
  Fold,
  Menu,
  Refresh,
  SwitchButton,
  User,
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { activeNavigationPath, navigationGroups } from '@/app/navigation'
import { useLayoutStore } from '@/app/stores/layout'
import { useSessionStore } from '@/app/stores/session'
import { changeMyPassword } from '@/features/identity/api'
import ChangeMyPasswordDialog from '@/features/identity/components/ChangeMyPasswordDialog.vue'

const route = useRoute()
const router = useRouter()
const layout = useLayoutStore()
const session = useSessionStore()
const passwordDialogVisible = ref(false)
const passwordSubmitting = ref(false)
const navigationCollapsed = computed(() => layout.collapsed)
const visibleNavigationGroups = computed(() =>
  navigationGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.permissionsAny?.length) {
          return item.permissionsAny.some((permission) => session.hasPermission(permission))
        }
        return session.hasPermission(item.permission)
      }),
    }))
    .filter((group) => group.items.length > 0),
)

const activePath = computed(() => activeNavigationPath(route.path))

function closeMobileMenuOnPrimaryNavigation(event: MouseEvent) {
  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
  layout.mobileMenuVisible = false
}

function refreshPage() {
  window.dispatchEvent(new CustomEvent('app:refresh'))
  ElMessage.success('数据已刷新')
}

function routeComponentKey(viewRoute: RouteLocationNormalizedLoaded) {
  return viewRoute.name === 'patient-detail' || viewRoute.name === 'patient-report-center' || viewRoute.name === 'report-ocr-review'
    ? viewRoute.path
    : viewRoute.fullPath
}

async function readNotification(id: string) {
  try {
    await session.markNotificationRead(id)
  } catch {
    ElMessage.error('通知状态更新失败')
  }
}

async function logout() {
  await session.logout()
  await router.replace('/login')
}

async function changePassword(payload: { currentPassword: string; newPassword: string }) {
  passwordSubmitting.value = true
  try {
    await changeMyPassword(payload.currentPassword, payload.newPassword)
    passwordDialogVisible.value = false
    ElMessage.success('密码已修改，请使用新密码重新登录')
    await session.logout()
    await router.replace('/login')
  } catch (reason) {
    ElMessage.error(reason instanceof Error ? reason.message : '密码修改失败')
  } finally {
    passwordSubmitting.value = false
  }
}

onMounted(() => {
  void session.initialize().catch(() => undefined)
})
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  background: var(--hy-page);
}

.sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 40;
  width: var(--hy-sidebar-width);
  display: flex;
  flex-direction: column;
  color: #b4bdcb;
  background: var(--hy-sidebar);
  border-right: 0;
  transition: width var(--hy-transition);
}

.is-collapsed .sidebar {
  width: var(--hy-sidebar-collapsed);
}

.brand {
  height: var(--hy-topbar-height);
  padding: 0 18px;
  border-bottom: 1px solid rgb(255 255 255 / 7%);
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  color: #fff;
  background: rgb(255 255 255 / 1.8%);
}

.brand__mark {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: grid;
  flex: 0 0 30px;
  place-items: center;
  background: var(--hy-primary);
  font-size: 15px;
  font-weight: 650;
}

.brand__name {
  min-width: 160px;
  font-size: 14px;
  font-weight: 650;
  white-space: nowrap;
}

.is-collapsed .brand {
  justify-content: center;
  padding: 0;
}

.is-collapsed .brand__name {
  display: none;
}

.sidebar__scroll {
  flex: 1;
  min-height: 0;
}

.sidebar__scroll :deep(.el-scrollbar__bar) {
  opacity: 1;
}

.sidebar__scroll :deep(.el-scrollbar__thumb) {
  background: rgb(255 255 255 / 42%);
}

.sidebar__scroll :deep(.el-scrollbar__thumb:hover) {
  background: rgb(255 255 255 / 65%);
}

.menu-group-title {
  padding: 14px 10px 6px;
  color: #8996aa;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.5px;
}

.sidebar-navigation {
  width: 100%;
  min-width: 0;
  padding: 12px;
}

.sidebar-menu-link {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  margin: 2px 0;
  padding: 0 10px;
  border-radius: 7px;
  gap: 10px;
  font-size: 13px;
  color: #b4bdcb;
  text-decoration: none;
}

.sidebar-menu-link :deep(.el-icon) {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  font-size: 18px;
}

.sidebar-menu-link:hover {
  color: #f7f9fc;
  background: var(--hy-sidebar-hover);
}

.sidebar-menu-link.is-active {
  color: #fff;
  background: rgb(87 108 255 / 22%);
  font-weight: 600;
}

.sidebar-menu-link.is-active::before {
  content: "";
  position: absolute;
  inset: 7px auto 7px -12px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: #7183ff;
}

.is-collapsed .sidebar-navigation {
  padding-inline: 10px;
}

.is-collapsed .sidebar-menu-link {
  justify-content: center;
  padding-inline: 8px;
}

.menu-badge {
  min-width: 18px;
  margin-left: auto;
  padding: 1px 6px;
  border-radius: 8px;
  color: #fff;
  background: var(--hy-danger);
  font-size: 10px;
  line-height: 15px;
  font-weight: 600;
  text-align: center;
}

.is-collapsed .menu-badge {
  position: absolute;
  top: 3px;
  right: 2px;
  min-width: 16px;
  padding-inline: 4px;
}

.sidebar-user {
  min-height: 64px;
  flex: 0 0 auto;
  padding: 12px 14px;
  border-top: 1px solid rgb(255 255 255 / 7%);
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  background: rgb(255 255 255 / 1.8%);
}

.sidebar-user .avatar,
.profile-button .avatar {
  background: var(--hy-primary);
  box-shadow: none;
}

.is-collapsed .sidebar-user {
  justify-content: center;
  padding-inline: 0;
}

.sidebar-user__text {
  min-width: 140px;
  display: grid;
}

.sidebar-user__text strong {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
}

.sidebar-user__text span {
  margin-top: 2px;
  color: #b4bdcb;
  font-size: 11px;
}

.app-main {
  width: calc(100% - var(--hy-sidebar-width));
  min-width: 0;
  margin-left: var(--hy-sidebar-width);
  transition:
    width var(--hy-transition),
    margin-left var(--hy-transition);
}

.is-collapsed .app-main {
  width: calc(100% - var(--hy-sidebar-collapsed));
  margin-left: var(--hy-sidebar-collapsed);
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  height: var(--hy-topbar-height);
  padding: 0 24px;
  border-bottom: 1px solid var(--hy-border);
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgb(255 255 255 / 96%);
}

.topbar-button {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  display: grid;
  place-items: center;
  color: var(--hy-text-secondary);
  background: transparent;
  cursor: pointer;
  transition: background var(--hy-transition);
}

.topbar-button:hover {
  color: var(--hy-text);
  background: #f2f3f5;
}

.mobile-menu-button {
  display: none;
}

.topbar__actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
}

.profile-button {
  height: 38px;
  padding: 0 8px 0 4px;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--hy-text);
  background: #f2f3f5;
  cursor: pointer;
}

.profile-button__name {
  font-size: 12px;
  font-weight: 550;
}

.app-content {
  width: 100%;
  min-height: calc(100vh - var(--hy-topbar-height));
  padding: 24px;
}

.notification-header {
  padding: 2px 2px 12px;
  border-bottom: 1px solid var(--hy-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notification-header span {
  color: var(--hy-text-muted);
  font-size: 11px;
}

.notification-list {
  max-height: 360px;
  overflow-y: auto;
}

.notification-item {
  width: 100%;
  padding: 12px 3px;
  border: 0;
  border-bottom: 1px solid var(--hy-border-light);
  display: flex;
  gap: 10px;
  color: inherit;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.notification-item:hover {
  background: var(--hy-primary-subtle);
}

.notification-item__dot {
  width: 7px;
  height: 7px;
  margin-top: 6px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: var(--hy-text-muted);
}

.notification-item__dot.is-danger {
  background: var(--hy-danger);
}

.notification-item__dot.is-warning {
  background: var(--hy-warning);
}

.notification-item__dot.is-info {
  background: var(--hy-info);
}

.notification-item__body {
  min-width: 0;
  display: grid;
}

.notification-item__body strong {
  font-size: 13px;
  font-weight: 600;
}

.notification-item__body span {
  margin-top: 3px;
  color: var(--hy-text-secondary);
  font-size: 12px;
  line-height: 1.5;
}

.notification-item__body small {
  margin-top: 5px;
  color: var(--hy-text-muted);
}

.notification-item:not(.is-unread) {
  opacity: 0.65;
}

.page-enter-active,
.page-leave-active {
  transition: opacity 140ms ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

.mobile-brand {
  height: 58px;
  padding: 0 16px;
  border-bottom: 1px solid var(--hy-border);
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--hy-text);
  font-size: 14px;
  font-weight: 650;
}

.mobile-navigation :deep(.el-drawer__body) {
  padding: 0;
  display: flex;
  flex-direction: column;
}

.mobile-navigation__scroll {
  flex: 1;
  min-height: 0;
}

.mobile-navigation__menu {
  padding: 8px 10px;
}

.mobile-menu-link {
  display: flex;
  align-items: center;
  width: 100%;
  height: 42px;
  margin: 2px 0;
  padding: 0 12px;
  border-radius: 7px;
  gap: 9px;
  color: var(--el-text-color-regular);
  text-decoration: none;
}

.mobile-menu-link:hover {
  color: var(--el-color-primary);
  background: var(--el-fill-color-light);
}

.mobile-menu-link.is-active {
  color: var(--hy-primary);
  background: var(--hy-primary-soft);
}

.mobile-menu-link :deep(.el-icon) {
  flex: 0 0 auto;
}

.mobile-menu-title {
  padding: 16px 10px 6px;
  color: var(--hy-text-muted);
  font-size: 11px;
  font-weight: 600;
}

.mobile-navigation__user {
  min-height: 66px;
  padding: 12px 16px;
  border-top: 1px solid var(--hy-border);
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-navigation__user > span:last-child {
  display: grid;
}

.mobile-navigation__user strong {
  font-size: 13px;
}

.mobile-navigation__user small {
  margin-top: 2px;
  color: var(--hy-text-muted);
}

@media (max-width: 960px) {
  .sidebar {
    width: var(--hy-sidebar-collapsed);
  }

  .brand {
    justify-content: center;
    padding: 0;
  }

  .brand__name,
  .menu-group-title,
  .sidebar-user__text {
    display: none;
  }

  .sidebar-menu-link {
    justify-content: center;
  }

  .app-main,
  .is-collapsed .app-main {
    width: calc(100% - var(--hy-sidebar-collapsed));
    margin-left: var(--hy-sidebar-collapsed);
  }

  .collapse-button {
    display: none;
  }

  .topbar :deep(.el-breadcrumb) {
    flex: 1;
  }
}

@media (max-width: 640px) {
  .sidebar {
    display: none;
  }

  .app-main,
  .is-collapsed .app-main {
    width: 100%;
    margin-left: 0;
  }

  .topbar {
    padding: 0 12px;
    gap: 9px;
  }

  .mobile-menu-button {
    display: grid;
  }

  .topbar :deep(.el-breadcrumb__item:first-child) {
    display: none;
  }

  .profile-button__name {
    display: none;
  }

  .app-content {
    padding: 14px;
  }
}
</style>
